(() => {
  const CARD_SELECTOR = "[data-pf-card]";
  const SECTION_SELECTOR = "[data-pf-collection-grid]";

  function toNumber(value, fallback = 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function parseVariants(node) {
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.textContent || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function buildVariantUrl(baseUrl, variantId) {
    if (!baseUrl || !variantId) return baseUrl;

    try {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set("variant", variantId);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (error) {
      const join = baseUrl.includes("?") ? "&" : "?";
      return `${baseUrl}${join}variant=${encodeURIComponent(variantId)}`;
    }
  }

  function matchesSelection(variant, selected) {
    if (!variant || !Array.isArray(variant.options)) return false;

    for (const [indexText, selectedValue] of Object.entries(selected)) {
      if (selectedValue === undefined || selectedValue === null || selectedValue === "") continue;

      const optionIndex = Number(indexText);
      const variantValue = variant.options[optionIndex];
      if (String(variantValue) !== String(selectedValue)) return false;
    }

    return true;
  }

  function findBestVariant(variants, selected, fallbackButton) {
    let found = variants.find((variant) => variant.available && matchesSelection(variant, selected));
    if (!found) found = variants.find((variant) => matchesSelection(variant, selected));
    if (found) return found;

    if (fallbackButton) {
      const optionIndex = toNumber(fallbackButton.dataset.pfOptionIndex, -1);
      const optionValue = fallbackButton.dataset.pfOptionValue;

      if (optionIndex >= 0) {
        found = variants.find(
          (variant) => variant.available && Array.isArray(variant.options) && String(variant.options[optionIndex]) === String(optionValue)
        );

        if (!found) {
          found = variants.find(
            (variant) => Array.isArray(variant.options) && String(variant.options[optionIndex]) === String(optionValue)
          );
        }
      }
    }

    return found || null;
  }

  function initCarousel(card) {
    const track = card.querySelector("[data-pf-track]");
    if (!track) return null;

    const slides = Array.from(card.querySelectorAll("[data-pf-slide]"));
    const totalSlides = Math.max(1, slides.length || toNumber(card.dataset.pfTotalSlides, 1));
    const prev = card.querySelector("[data-pf-prev]");
    const next = card.querySelector("[data-pf-next]");
    const counterCurrent = card.querySelector("[data-pf-counter-current]");
    let activeIndex = 0;
    let wheelLastEventAt = 0;
    let wheelDirection = 0;
    let wheelGestureConsumed = false;

    function currentIndex() {
      return Math.min(totalSlides - 1, Math.max(0, activeIndex));
    }

    function markInteracted() {
      if (card.dataset.pfSliderInteracted !== "true") {
        card.dataset.pfSliderInteracted = "true";
      }
    }

    function updateState() {
      const index = currentIndex();
      card.dataset.pfCurrentIndex = String(index);

      if (counterCurrent) counterCurrent.textContent = String(index + 1);
      if (prev) prev.disabled = totalSlides <= 1;
      if (next) next.disabled = totalSlides <= 1;
    }

    function goTo(index, behavior = "smooth") {
      const width = track.clientWidth || 1;
      const safeIndex = Math.min(totalSlides - 1, Math.max(0, index));
      activeIndex = safeIndex;

      if (behavior === "instant") {
        const previousScrollBehavior = track.style.scrollBehavior;
        track.style.scrollBehavior = "auto";
        track.scrollLeft = safeIndex * width;
        track.style.scrollBehavior = previousScrollBehavior;
        updateState();
        return;
      }

      track.scrollTo({ left: safeIndex * width, behavior });
    }

    function goStep(direction) {
      const index = currentIndex();
      if (direction > 0) {
        const wraps = index >= totalSlides - 1;
        const nextIndex = wraps ? 0 : index + 1;
        goTo(nextIndex, wraps ? "instant" : "smooth");
        return;
      }

      const wraps = index <= 0;
      const nextIndex = wraps ? totalSlides - 1 : index - 1;
      goTo(nextIndex, wraps ? "instant" : "smooth");
    }

    if (prev) {
      prev.addEventListener("click", () => {
        markInteracted();
        goStep(-1);
      });
    }
    if (next) {
      next.addEventListener("click", () => {
        markInteracted();
        goStep(1);
      });
    }

    track.addEventListener("pointerdown", markInteracted, { passive: true });
    track.addEventListener("mousedown", markInteracted, { passive: true });
    track.addEventListener("touchstart", markInteracted, { passive: true });
    track.addEventListener(
      "wheel",
      (event) => {
        if (totalSlides <= 1) return;

        const now = performance.now();
        const absX = Math.abs(event.deltaX);
        const absY = Math.abs(event.deltaY);
        const horizontalIntent = absX > 8 && absX > absY * 1.05;
        const withinGestureWindow = now - wheelLastEventAt < 320;
        const shouldConsumeMomentum = withinGestureWindow && wheelGestureConsumed;

        if (!horizontalIntent && !shouldConsumeMomentum) return;
        event.preventDefault();

        if (!horizontalIntent) {
          wheelLastEventAt = now;
          return;
        }

        const movingRight = event.deltaX > 0;
        const direction = movingRight ? 1 : -1;
        const isNewGesture = !withinGestureWindow || direction !== wheelDirection;

        if (isNewGesture) {
          wheelDirection = direction;
          wheelGestureConsumed = false;
        }

        wheelLastEventAt = now;
        if (wheelGestureConsumed) return;

        wheelGestureConsumed = true;
        markInteracted();
        goStep(direction);
      },
      { passive: false }
    );

    let ticking = false;
    track.addEventListener(
      "scroll",
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const width = track.clientWidth || 1;
          const index = Math.round(track.scrollLeft / width);
          activeIndex = Math.min(totalSlides - 1, Math.max(0, index));
          updateState();
        });
      },
      { passive: true }
    );

    if (window.ResizeObserver) {
      const observer = new ResizeObserver(() => updateState());
      observer.observe(track);
    }

    goTo(0, "instant");
    updateState();

    return {
      goTo,
      updateState,
      currentIndex,
    };
  }

  function initVariantOptions(card, carouselApi) {
    const variantsScript = card.querySelector("[data-pf-variants]");
    const variants = parseVariants(variantsScript);
    if (!variants.length) return;

    const selected = {};
    const optionButtons = Array.from(card.querySelectorAll("[data-pf-option-btn]"));
    const productLinks = card.querySelectorAll("[data-pf-product-link]");
    const quickAddInput = card.querySelector("[data-pf-quick-add-input]");
    const mainImage = card.querySelector("[data-pf-main-slide] img");
    const priceNode = card.querySelector("[data-pf-price]");
    const compareNode = card.querySelector("[data-pf-compare]");
    const baseUrl = card.dataset.pfBaseUrl || "";
    let activeVariant = null;

    optionButtons.forEach((button) => {
      if (button.classList.contains("is-active")) {
        const optionIndex = toNumber(button.dataset.pfOptionIndex, -1);
        if (optionIndex >= 0) selected[optionIndex] = button.dataset.pfOptionValue;
      }
    });

    const initialVariantId = toNumber(card.dataset.pfInitialVariant, 0);
    if (initialVariantId > 0) {
      activeVariant = variants.find((variant) => Number(variant.id) === initialVariantId) || null;
    }

    if (!activeVariant) {
      activeVariant = findBestVariant(variants, selected, null);
    }

    if (activeVariant && Array.isArray(activeVariant.options)) {
      activeVariant.options.forEach((value, index) => {
        selected[index] = value;
      });
    }

    function updateOptionButtons() {
      optionButtons.forEach((button) => {
        const optionIndex = toNumber(button.dataset.pfOptionIndex, -1);
        const optionValue = button.dataset.pfOptionValue;

        if (optionIndex < 0 || optionValue == null) return;

        const candidateSelection = { ...selected, [optionIndex]: optionValue };
        const existsCombination = variants.some((variant) => matchesSelection(variant, candidateSelection));
        const existsAvailable = variants.some(
          (variant) => variant.available && matchesSelection(variant, candidateSelection)
        );

        button.disabled = !existsCombination;
        button.classList.toggle("is-unavailable", existsCombination && !existsAvailable);
        button.setAttribute("aria-disabled", button.disabled ? "true" : "false");

        let isActive = false;
        if (activeVariant && Array.isArray(activeVariant.options)) {
          isActive = String(activeVariant.options[optionIndex]) === String(optionValue);
        } else if (String(selected[optionIndex]) === String(optionValue)) {
          isActive = true;
        }

        button.classList.toggle("is-active", isActive);
        button.setAttribute("aria-pressed", isActive ? "true" : "false");
      });
    }

    function applyVariant(variant, swapImage = true) {
      if (!variant) return;

      activeVariant = variant;

      if (Array.isArray(variant.options)) {
        variant.options.forEach((value, index) => {
          selected[index] = value;
        });
      }

      if (variant.id) {
        const nextHref = buildVariantUrl(baseUrl, variant.id);
        productLinks.forEach((link) => link.setAttribute("href", nextHref));
        if (quickAddInput) quickAddInput.value = variant.id;
      }

      if (swapImage && variant.image && mainImage) {
        mainImage.src = variant.image;
        mainImage.srcset = `${variant.image} 2200w`;
      }

      if (priceNode && variant.price) {
        priceNode.textContent = variant.price;
      }

      if (compareNode) {
        if (variant.compare) {
          compareNode.textContent = variant.compare;
          compareNode.hidden = false;
        } else {
          compareNode.hidden = true;
        }
      }

      updateOptionButtons();

      if (carouselApi) {
        carouselApi.goTo(0, swapImage ? "smooth" : "instant");
        carouselApi.updateState();
      }
    }

    optionButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const optionIndex = toNumber(button.dataset.pfOptionIndex, -1);
        const optionValue = button.dataset.pfOptionValue;

        if (optionIndex < 0 || optionValue == null) return;

        selected[optionIndex] = optionValue;

        const variant = findBestVariant(variants, selected, button);
        applyVariant(variant, true);
      });
    });

    updateOptionButtons();

    if (activeVariant) {
      applyVariant(activeVariant, false);
    }
  }

  function initCard(card) {
    if (!card || card.dataset.pfInit === "true") return;
    card.dataset.pfInit = "true";

    const carouselApi = initCarousel(card);
    initVariantOptions(card, carouselApi);
    initHoverToSecond(card, carouselApi);
  }

  function initHoverToSecond(card, carouselApi) {
    if (!card || !carouselApi) return;
    if (!card.classList.contains("pf-card--hover-secondary")) return;

    const totalSlides = toNumber(card.dataset.pfTotalSlides, 1);
    if (totalSlides < 2) return;

    const mediaQuery = window.matchMedia("(hover: hover) and (min-width: 990px)");
    let enteredFromFirstSlide = false;

    card.addEventListener("mouseenter", () => {
      if (!mediaQuery.matches) return;

      const current = carouselApi.currentIndex();
      const sliderInteracted = card.dataset.pfSliderInteracted === "true";

      enteredFromFirstSlide = current === 0 && !sliderInteracted;

      if (enteredFromFirstSlide) {
        carouselApi.goTo(1, "smooth");
      }
    });

    card.addEventListener("mouseleave", () => {
      if (!mediaQuery.matches) return;

      const sliderInteracted = card.dataset.pfSliderInteracted === "true";

      if (enteredFromFirstSlide && !sliderInteracted) {
        carouselApi.goTo(0, "smooth");
      }

      enteredFromFirstSlide = false;
    });
  }

  function initFilterDrawer(section) {
    if (!section || section.dataset.pfFiltersInit === "true") return;

    const drawer = section.querySelector("[data-pf-filter-drawer]");
    const overlay = section.querySelector("[data-pf-filter-overlay]");
    const openButtons = section.querySelectorAll("[data-pf-filter-open]");
    const closeButtons = section.querySelectorAll("[data-pf-filter-close]");

    if (!drawer || !overlay || !openButtons.length) return;

    section.dataset.pfFiltersInit = "true";
    if (!drawer.classList.contains("is-open")) {
      document.body.classList.remove("pf-no-scroll");
    }

    function closeDrawer() {
      drawer.classList.remove("is-open");
      overlay.classList.remove("is-open");
      drawer.setAttribute("aria-hidden", "true");
      document.body.classList.remove("pf-no-scroll");
    }

    function openDrawer() {
      drawer.classList.add("is-open");
      overlay.classList.add("is-open");
      drawer.setAttribute("aria-hidden", "false");
      document.body.classList.add("pf-no-scroll");
    }

    openButtons.forEach((button) => {
      button.addEventListener("click", openDrawer);
    });

    closeButtons.forEach((button) => {
      button.addEventListener("click", closeDrawer);
    });

    overlay.addEventListener("click", closeDrawer);

    drawer.addEventListener("click", (event) => {
      const link = event.target.closest("a[data-pf-filter-link]");
      if (!link) return;
      if (event.defaultPrevented) return;
      if (event.button !== 0 && event.button !== undefined) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      closeDrawer();
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && drawer.classList.contains("is-open")) {
        closeDrawer();
      }
    });

    const form = drawer.querySelector(".pf-filter-form");
    if (form) {
      form.addEventListener("submit", () => {
        closeDrawer();
      });
    }
  }

  function initSortForms(root) {
    const context = root || document;
    const forms = context.querySelectorAll?.("[data-pf-sort-form]") || [];

    forms.forEach((form) => {
      if (form.dataset.pfInit === "true") return;
      form.dataset.pfInit = "true";

      const select = form.querySelector("select[name='sort_by']");
      if (!select) return;

      select.addEventListener("change", () => {
        form.submit();
      });
    });
  }

  function initAll(root) {
    const context = root || document;

    if (context.matches && context.matches(CARD_SELECTOR)) {
      initCard(context);
    }
    context.querySelectorAll?.(CARD_SELECTOR).forEach((card) => {
      initCard(card);
    });

    if (context.matches && context.matches(SECTION_SELECTOR)) {
      initFilterDrawer(context);
    }
    context.querySelectorAll?.(SECTION_SELECTOR).forEach((section) => {
      initFilterDrawer(section);
    });

    initSortForms(context);
  }

  document.addEventListener("DOMContentLoaded", () => {
    document.body.classList.remove("pf-no-scroll");
    initAll(document);
  });

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });

  window.addEventListener("pageshow", () => {
    document.body.classList.remove("pf-no-scroll");
  });
})();
